import { saveAs } from 'file-saver';
import { EXCEL_THEME, MONEY_NUM_FMT, PCT_NUM_FMT } from './excelTheme';
import { fitHillRoasModel, hasCohortSheet } from './roasSaturation';
import { resolveDealIrrModelInputs } from './resolveDealIrrModelInputs';

export const DEAL_IRR_SHEETS = {
  ROAS: 'ROAS vector',
  LTV: 'LTV curve',
  IRR_ROAS: 'IRR Summary ROAS',
  IRR_HILL: 'IRR Summary Hill',
};

const FIRST_MONTH_COL = 4;
const CALC_LABEL_COL = 4;
const CALC_VALUE_COL = 5;
const MAX_CALENDAR_MONTHS = 72;
const DATE_NUM_FMT = 'MMM YYYY';
const INPUT_FILL = 'FFDCE6F1';
const INPUT_FONT = 'FF1F4E79';
const OUTPUT_FILL = 'FFF8CBAD';
const MULTIPLIER_NUM_FMT = '0.00"x"';
const IRR_SUMMARY_LABEL_COL = 2;
const IRR_SUMMARY_VALUE_COL = 3;
const IRR_SUMMARY_BASIS_COL = 4;
const IRR_SUMMARY_IRR_COL = 5;
const IRR_SUMMARY_YEAR_START_COL = 4;
const IRR_SUMMARY_MAX_YEARS = 8;

const ROWS = {
  paramHeader: 1,
  calcHeader: 1,
  commitment: 2,
  maxExposureCalc: 2,
  maxExposure: 3,
  avgExposure: 3,
  maxMonthlyDraw: 4,
  outflow: 4,
  fundingCap: 5,
  inflow: 5,
  maxDrawIncrease: 6,
  totalFacilityFee: 6,
  cogs: 7,
  irr: 7,
  grace: 8,
  marketingGrace: 19,
  netCash: 8,
  facilityFeePct: 10,
  facilityFeeStart: 11,
  facilityFeeInstallments: 12,
  revShare: 13,
  facilitySize: 14,
  firstMarketingMonth: 15,
  learning: 16,
  k: 17,
  kMessage: 18,
  calendarHeader: 21,
  monthHeader: 22,
  monthlyIrrPlan: 23,
  marketing: 24,
  curve: 25,
  incremental: 26,
  fundedIrrPlan: 28,
  saturation: 27,
  customerHeader: 29,
  customerMarketing: 30,
  customerGross: 31,
  customerNet: 32,
  twelveFundingPct: 33,
  customerFunding: 34,
  customerRepayment: 35,
  customerFees: 36,
  customerNetCf: 37,
  twelveHeader: 39,
  twelveOrigination: 40,
  twelveRepayment: 41,
  twelveFee: 42,
  twelveNet: 43,
  interest: 45,
  exposure: 46,
  drawHelpersHeader: 49,
  fundableMarketing: 50,
  sameMonthRepaymentCredit: 51,
  drawExposureHeadroom: 52,
  cohortHeader: 54,
  cohortStart: 55,
};

const COHORT_TABLE_GAP = 3;

export function dealIrrCohortTableRows(originCount, includeLtvHelpers = false) {
  const stride = originCount + 1 + COHORT_TABLE_GAP;
  let headerRow = ROWS.cohortHeader;
  let startRow = ROWS.cohortStart;
  const tables = {};

  if (includeLtvHelpers) {
    tables.saturationHeader = headerRow;
    tables.saturationStart = startRow;
    headerRow += stride;
    startRow += stride;
  }

  tables.grossRevenueHeader = headerRow;
  tables.grossRevenueStart = startRow;
  headerRow += stride;
  startRow += stride;

  tables.revenueShareHeader = headerRow;
  tables.revenueShareStart = startRow;
  headerRow += stride;
  startRow += stride;
  tables.exposureHeader = headerRow;
  tables.exposureStart = startRow;
  headerRow += stride;
  startRow += stride;
  tables.feeHeader = headerRow;
  tables.feeStart = startRow;
  tables.feeTotal = startRow + originCount;
  tables.laggedShareHeader = tables.feeTotal + 1 + COHORT_TABLE_GAP;
  tables.laggedShareStart = tables.laggedShareHeader + 1;
  headerRow = tables.laggedShareHeader;
  startRow = tables.laggedShareStart;
  headerRow += stride;
  startRow += stride;
  tables.preDrawCreditHeader = headerRow;
  tables.preDrawCreditStart = startRow;
  headerRow += stride;
  startRow += stride;
  tables.repaymentHeader = headerRow;
  tables.repaymentStart = startRow;
  return tables;
}

function colLetter(col) {
  let n = col;
  let out = '';
  while (n > 0) {
    const rem = (n - 1) % 26;
    out = String.fromCharCode(65 + rem) + out;
    n = Math.floor((n - 1) / 26);
  }
  return out;
}

function monthCell(row, monthIndex) {
  return `${colLetter(FIRST_MONTH_COL + monthIndex)}${row}`;
}

function monthRange(row, monthCount) {
  return `${monthCell(row, 0)}:${monthCell(row, monthCount - 1)}`;
}

function styleInputCell(cell, numFmt) {
  cell.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: INPUT_FILL },
  };
  cell.font = { color: { argb: INPUT_FONT }, bold: true };
  if (numFmt) {
    cell.numFmt = numFmt;
  }
}

function styleOutputCell(cell, numFmt) {
  cell.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: OUTPUT_FILL },
  };
  cell.font = { bold: true, color: { argb: EXCEL_THEME.sectionText } };
  if (numFmt) {
    cell.numFmt = numFmt;
  }
}

function writeLabel(sheet, row, label) {
  const cell = sheet.getCell(row, 1);
  cell.value = label;
  cell.font = { color: { argb: EXCEL_THEME.sectionText } };
}

function writeCalcLabel(sheet, row, label) {
  const cell = sheet.getCell(row, CALC_LABEL_COL);
  cell.value = label;
  cell.font = { color: { argb: EXCEL_THEME.sectionText } };
}

function outlineRange(sheet, startRow, startCol, endRow, endCol) {
  const edge = { style: 'medium', color: { argb: EXCEL_THEME.sectionText } };
  for (let row = startRow; row <= endRow; row += 1) {
    for (let col = startCol; col <= endCol; col += 1) {
      const border = {};
      if (row === startRow) {
        border.top = edge;
      }
      if (row === endRow) {
        border.bottom = edge;
      }
      if (col === startCol) {
        border.left = edge;
      }
      if (col === endCol) {
        border.right = edge;
      }
      sheet.getCell(row, col).border = border;
    }
  }
}

function writeInputValue(sheet, row, label, value, numFmt) {
  writeLabel(sheet, row, label);
  const cell = sheet.getCell(row, 2);
  if (value == null) {
    cell.value = null;
  } else {
    cell.value = value;
  }
  styleInputCell(cell, numFmt);
}

function writeFormula(sheet, row, col, formula, numFmt) {
  const cell = sheet.getCell(row, col);
  cell.value = { formula };
  if (numFmt) {
    cell.numFmt = numFmt;
  }
  return cell;
}

function capOrUnbounded(cellRef) {
  return `IF(${cellRef}="",1E+12,${cellRef})`;
}

function resolveMonthCount(inputs) {
  const curveLen = Math.max(
    inputs.cumulativeRoas.length,
    inputs.maxRoasByMob.length,
    12
  );
  return Math.min(
    MAX_CALENDAR_MONTHS,
    Math.max(1, inputs.marketing.length + curveLen)
  );
}

function writeSharedInputs(sheet, inputs, { includeHill }) {
  sheet.getCell(1, 1).value = 'Parameter';
  sheet.getCell(1, 2).value = 'Value';
  sheet.getCell(1, 1).font = { bold: true };
  sheet.getCell(1, 2).font = { bold: true };

  writeInputValue(sheet, ROWS.commitment, 'Commitment Period (months)', inputs.commitmentMonths);
  writeInputValue(
    sheet,
    ROWS.maxExposure,
    'Max Exposure ($)',
    inputs.maxExposure,
    MONEY_NUM_FMT
  );
  writeInputValue(
    sheet,
    ROWS.maxMonthlyDraw,
    'Max monthly draw ($)',
    inputs.maxMonthlyDraw,
    MONEY_NUM_FMT
  );
  writeInputValue(sheet, ROWS.fundingCap, 'Funding Cap %', inputs.fundingCap, PCT_NUM_FMT);
  writeInputValue(
    sheet,
    ROWS.maxDrawIncrease,
    'Max draw increase %',
    inputs.maxDrawIncrease,
    PCT_NUM_FMT
  );
  writeInputValue(sheet, ROWS.cogs, 'COGS %', inputs.cogs, PCT_NUM_FMT);
  writeInputValue(sheet, ROWS.grace, 'Repayment grace (months)', inputs.grace);
  writeInputValue(
    sheet,
    ROWS.marketingGrace,
    'Marketing spend grace (months)',
    inputs.marketingGrace
  );
  writeInputValue(sheet, ROWS.facilityFeePct, 'Facility Fee %', inputs.facilityFeePct, PCT_NUM_FMT);
  writeInputValue(
    sheet,
    ROWS.facilityFeeStart,
    'Facility Fee starting (month)',
    inputs.facilityFeeStart
  );
  writeInputValue(
    sheet,
    ROWS.facilityFeeInstallments,
    'Facility Fee installments',
    inputs.facilityFeeInstallments
  );
  writeInputValue(sheet, ROWS.revShare, 'Max revenue share %', inputs.revShare, PCT_NUM_FMT);
  writeInputValue(sheet, ROWS.facilitySize, 'Facility size ($)', inputs.facilitySize, MONEY_NUM_FMT);
  writeInputValue(
    sheet,
    ROWS.firstMarketingMonth,
    'First marketing month',
    inputs.firstMarketingMonth,
    DATE_NUM_FMT
  );

  if (includeHill) {
    writeInputValue(sheet, ROWS.learning, 'Learning factor', inputs.learning, PCT_NUM_FMT);
    writeInputValue(sheet, ROWS.k, 'K (Hill)', inputs.k, MONEY_NUM_FMT);
    writeLabel(sheet, ROWS.kMessage, 'Hill status');
    const messageCell = sheet.getCell(ROWS.kMessage, 2);
    messageCell.value = inputs.hillMessage;
    messageCell.font = {
      italic: true,
      color: { argb: inputs.hillMatched ? EXCEL_THEME.annotation : 'FFB22A2A' },
    };
    sheet.mergeCells(ROWS.kMessage, 2, ROWS.kMessage, 6);
  }
}

function writeOutputFormulas(sheet, monthCount) {
  const header = sheet.getCell(ROWS.calcHeader, CALC_LABEL_COL);
  header.value = 'Calculated';
  header.font = { bold: true };
  const valueHeader = sheet.getCell(ROWS.calcHeader, CALC_VALUE_COL);
  valueHeader.value = 'Value';
  valueHeader.font = { bold: true };

  writeCalcLabel(sheet, ROWS.maxExposureCalc, 'Max Exposure calc ($)');
  styleOutputCell(
    writeFormula(
      sheet,
      ROWS.maxExposureCalc,
      CALC_VALUE_COL,
      `IFERROR(MAX(${monthRange(ROWS.exposure, monthCount)}),0)`,
      MONEY_NUM_FMT
    )
  );

  writeCalcLabel(sheet, ROWS.avgExposure, 'Avg Exposure ($)');
  styleOutputCell(
    writeFormula(
      sheet,
      ROWS.avgExposure,
      CALC_VALUE_COL,
      `IFERROR(AVERAGEIF(${monthRange(ROWS.exposure, monthCount)},">0"),0)`,
      MONEY_NUM_FMT
    )
  );

  writeCalcLabel(sheet, ROWS.outflow, 'Total Growth Investment');
  styleOutputCell(
    writeFormula(
      sheet,
      ROWS.outflow,
      CALC_VALUE_COL,
      `SUM(${monthRange(ROWS.twelveOrigination, monthCount)})`,
      MONEY_NUM_FMT
    )
  );

  writeCalcLabel(sheet, ROWS.inflow, 'Total Returns');
  styleOutputCell(
    writeFormula(
      sheet,
      ROWS.inflow,
      CALC_VALUE_COL,
      `SUM(${monthRange(ROWS.twelveRepayment, monthCount)},${monthRange(ROWS.twelveFee, monthCount)})`,
      MONEY_NUM_FMT
    )
  );

  writeCalcLabel(sheet, ROWS.totalFacilityFee, 'Total Facility Fee');
  styleOutputCell(
    writeFormula(
      sheet,
      ROWS.totalFacilityFee,
      CALC_VALUE_COL,
      `$B$${ROWS.facilitySize}*$B$${ROWS.facilityFeePct}`,
      MONEY_NUM_FMT
    )
  );

  writeCalcLabel(sheet, ROWS.irr, 'IRR');
  styleOutputCell(
    writeFormula(
      sheet,
      ROWS.irr,
      CALC_VALUE_COL,
      `IFERROR((1+IRR(${monthRange(ROWS.twelveNet, monthCount)}))^12-1,"")`,
      PCT_NUM_FMT
    )
  );

  writeCalcLabel(sheet, ROWS.netCash, 'Net cash');
  styleOutputCell(
    writeFormula(
      sheet,
      ROWS.netCash,
      CALC_VALUE_COL,
      `SUM(${monthRange(ROWS.twelveNet, monthCount)})`,
      MONEY_NUM_FMT
    )
  );

  outlineRange(
    sheet,
    ROWS.calcHeader,
    CALC_LABEL_COL,
    ROWS.netCash,
    CALC_VALUE_COL
  );
}

function cohortTables(originCount, mode) {
  return dealIrrCohortTableRows(originCount, mode === 'ltv');
}

function cohortShareRow(origin, originCount, mode) {
  return cohortTables(originCount, mode).revenueShareStart + origin;
}

function cohortExposureRow(origin, originCount, mode) {
  return cohortTables(originCount, mode).exposureStart + origin;
}

function cohortFeeRow(origin, originCount, mode) {
  return cohortTables(originCount, mode).feeStart + origin;
}

function cohortAppliedRow(origin, originCount, mode) {
  return cohortTables(originCount, mode).repaymentStart + origin;
}

function cohortLaggedShareRow(origin, originCount, mode) {
  return cohortTables(originCount, mode).laggedShareStart + origin;
}

function cohortPreDrawCreditRow(origin, originCount, mode) {
  return cohortTables(originCount, mode).preDrawCreditStart + origin;
}

function cohortColumnRange(startRow, monthIndex, originCount) {
  return `${monthCell(startRow, monthIndex)}:${monthCell(startRow + originCount - 1, monthIndex)}`;
}

function monthIndexRef(monthIndex) {
  return monthCell(ROWS.monthHeader, monthIndex);
}

function prevMonthCell(row, monthIndex) {
  const current = monthCell(row, monthIndex);
  const idx = monthIndexRef(monthIndex);
  return `IF(${idx}=0,0,OFFSET(${current},0,-1))`;
}

function planMarketingRange(monthCount) {
  return monthRange(ROWS.marketing, monthCount);
}

function customerMarketingOutflowFormula(monthIndex, monthCount) {
  const idx = monthIndexRef(monthIndex);
  const origin = `${idx}-$B$${ROWS.marketingGrace}`;
  const marketing = planMarketingRange(monthCount);
  return `-IFERROR(IF(${idx}>=$B$${ROWS.marketingGrace},INDEX(${marketing},1,${origin}+1),0),0)`;
}

function committedMarketingDueFormula(monthIndex, monthCount) {
  const idx = monthIndexRef(monthIndex);
  const origin = `${idx}-$B$${ROWS.marketingGrace}`;
  const marketing = planMarketingRange(monthCount);
  return `IF(AND(${idx}>=$B$${ROWS.marketingGrace},${origin}<$B$${ROWS.commitment}),IFERROR(INDEX(${marketing},1,${origin}+1),0),0)`;
}

function cohortLaggedRevenueShareFormula(origin, monthIndex, monthCount, originCount, mode) {
  const idx = monthIndexRef(monthIndex);
  const startMonth = `${origin}+$B$${ROWS.marketingGrace}+$B$${ROWS.grace}`;
  const revenueMonth = `${origin}+(${idx}-(${startMonth}))`;
  const shareRange = monthRange(cohortShareRow(origin, originCount, mode), monthCount);
  return `IF(${idx}<${startMonth},0,IFERROR(INDEX(${shareRange},1,${revenueMonth}+1),0))`;
}

function cohortPreDrawRepaymentCreditFormula(origin, monthIndex, originCount, mode) {
  const idx = monthIndexRef(monthIndex);
  const fundingMonth = `${origin}+$B$${ROWS.marketingGrace}`;
  const prevExposure = prevMonthCell(cohortExposureRow(origin, originCount, mode), monthIndex);
  const fee = monthCell(cohortFeeRow(origin, originCount, mode), monthIndex);
  const laggedShare = monthCell(cohortLaggedShareRow(origin, originCount, mode), monthIndex);
  return `IF(${idx}<=${fundingMonth},0,MIN(${laggedShare},MAX(0,${prevExposure}+${fee})))`;
}

function sameMonthRepaymentCreditSummaryFormula(monthIndex, originCount, mode) {
  const tables = cohortTables(originCount, mode);
  return `SUM(${cohortColumnRange(tables.preDrawCreditStart, monthIndex, originCount)})`;
}

function drawExposureHeadroomFormula(monthIndex) {
  const prevExposure = prevMonthCell(ROWS.exposure, monthIndex);
  const interest = monthCell(ROWS.interest, monthIndex);
  const credit = monthCell(ROWS.sameMonthRepaymentCredit, monthIndex);
  return `MAX(0,${capOrUnbounded(`$B$${ROWS.maxExposure}`)}-${prevExposure}-${interest}+${credit})`;
}

function drawFormula(monthIndex) {
  const fundable = monthCell(ROWS.fundableMarketing, monthIndex);
  const headroom = monthCell(ROWS.drawExposureHeadroom, monthIndex);
  const prevDraw = prevMonthCell(ROWS.customerFunding, monthIndex);
  const momCap = `IF(OR($B$${ROWS.maxDrawIncrease}="",${prevDraw}=0),1E+12,${prevDraw}*(1+$B$${ROWS.maxDrawIncrease}))`;
  return `IF(${fundable}=0,0,MAX(0,MIN(${fundable}*$B$${ROWS.fundingCap},${capOrUnbounded(`$B$${ROWS.maxMonthlyDraw}`)},${headroom},${momCap})))`;
}

function facilityFeeFormula(monthIndex) {
  return `IF(OR($B$${ROWS.facilityFeeInstallments}<=0,$B$${ROWS.facilitySize}=0),0,IF(AND(${monthIndex}>=ROUND($B$${ROWS.facilityFeeStart},0),${monthIndex}<ROUND($B$${ROWS.facilityFeeStart},0)+$B$${ROWS.facilityFeeInstallments}),$B$${ROWS.facilitySize}*$B$${ROWS.facilityFeePct}/$B$${ROWS.facilityFeeInstallments},0))`;
}

function twelveFundingPctFormula(monthIndex) {
  const marketingOutflow = monthCell(ROWS.customerMarketing, monthIndex);
  const funding = monthCell(ROWS.customerFunding, monthIndex);
  return `IF(OR(${marketingOutflow}=0,${funding}=0),0,${funding}/ABS(${marketingOutflow}))`;
}

function styleMonthHeaderCell(cell) {
  cell.font = { bold: true, color: { argb: EXCEL_THEME.brandText } };
  cell.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: EXCEL_THEME.brand },
  };
  cell.alignment = { horizontal: 'center' };
}

function calendarMonthFormula(monthIndex) {
  return `EDATE($B$${ROWS.firstMarketingMonth},${monthIndexRef(monthIndex)})`;
}

function writeTimelineHeaders(sheet, monthCount) {
  writeLabel(sheet, ROWS.calendarHeader, 'Calendar month');
  writeLabel(sheet, ROWS.monthHeader, 'Index');
  for (let m = 0; m < monthCount; m += 1) {
    const indexCell = writeFormula(
      sheet,
      ROWS.monthHeader,
      FIRST_MONTH_COL + m,
      `COLUMN()-${FIRST_MONTH_COL}`
    );
    styleMonthHeaderCell(indexCell);
    const calendarCell = writeFormula(
      sheet,
      ROWS.calendarHeader,
      FIRST_MONTH_COL + m,
      calendarMonthFormula(m),
      DATE_NUM_FMT
    );
    styleMonthHeaderCell(calendarCell);
    if (
      FIRST_MONTH_COL + m !== CALC_LABEL_COL &&
      FIRST_MONTH_COL + m !== CALC_VALUE_COL
    ) {
      sheet.getColumn(FIRST_MONTH_COL + m).width = 14;
    }
  }
}

function writeMarketingRow(sheet, marketing, monthCount) {
  writeLabel(sheet, ROWS.marketing, 'Marketing spend');
  for (let m = 0; m < monthCount; m += 1) {
    const cell = sheet.getCell(ROWS.marketing, FIRST_MONTH_COL + m);
    cell.value = m < marketing.length ? marketing[m] : 0;
    styleInputCell(cell, MONEY_NUM_FMT);
  }
}

function monthlyIrrPlanRate(inputs, monthIndex) {
  const perMonth = inputs.monthlyIrrByMonth?.[monthIndex];
  if (perMonth != null && Number.isFinite(Number(perMonth)) && Number(perMonth) > 0) {
    return Number(perMonth);
  }
  return Number(inputs.monthlyIrr) || 0;
}

function writeMonthlyIrrPlanRow(sheet, inputs, monthCount) {
  writeLabel(sheet, ROWS.monthlyIrrPlan, 'IRR — fee % / month');
  for (let m = 0; m < monthCount; m += 1) {
    const cell = sheet.getCell(ROWS.monthlyIrrPlan, FIRST_MONTH_COL + m);
    cell.value = monthlyIrrPlanRate(inputs, m);
    styleInputCell(cell, PCT_NUM_FMT);
  }
}

function incrementalRoasFormula(monthIndex) {
  const current = monthCell(ROWS.curve, monthIndex);
  if (monthIndex === 0) {
    return `IF(OR(${current}="",${current}=0),0,${current})`;
  }
  const previous = monthCell(ROWS.curve, monthIndex - 1);
  return `IF(OR(${current}="",${current}=0),0,${current}-${previous})`;
}

function fundedIrrPlanCellFormula(origin) {
  const irr = monthCell(ROWS.monthlyIrrPlan, origin);
  const marketing = monthCell(ROWS.marketing, origin);
  const draw = `OFFSET(${monthCell(ROWS.customerFunding, origin)},0,$B$${ROWS.marketingGrace})`;
  return `IF(AND(${origin}<$B$${ROWS.commitment},${marketing}>0,${draw}>0),${irr},"")`;
}

function writeFundedIrrPlanRow(sheet, originCount) {
  writeLabel(sheet, ROWS.fundedIrrPlan, 'Funded cohort IRR (summary)');
  for (let origin = 0; origin < originCount; origin += 1) {
    writeFormula(
      sheet,
      ROWS.fundedIrrPlan,
      FIRST_MONTH_COL + origin,
      fundedIrrPlanCellFormula(origin),
      PCT_NUM_FMT
    );
  }
}

function writeRoasCurve(sheet, cumulativeRoas, monthCount) {
  writeLabel(sheet, ROWS.curve, 'Cumulative ROAS');
  writeLabel(sheet, ROWS.incremental, 'Incremental ROAS');
  for (let m = 0; m < monthCount; m += 1) {
    const cumCell = sheet.getCell(ROWS.curve, FIRST_MONTH_COL + m);
    cumCell.value = m < cumulativeRoas.length ? cumulativeRoas[m] : 0;
    styleInputCell(cumCell, PCT_NUM_FMT);
    const incr = writeFormula(
      sheet,
      ROWS.incremental,
      FIRST_MONTH_COL + m,
      incrementalRoasFormula(m),
      PCT_NUM_FMT
    );
    incr.font = { color: { argb: EXCEL_THEME.sectionText } };
  }
}

function writeLtvCurve(sheet, inputs, monthCount) {
  writeLabel(sheet, ROWS.curve, 'Max ROAS (incremental)');
  for (let m = 0; m < monthCount; m += 1) {
    const maxCell = sheet.getCell(ROWS.curve, FIRST_MONTH_COL + m);
    maxCell.value =
      inputs.hillMatched && m < inputs.maxRoasByMob.length
        ? inputs.maxRoasByMob[m]
        : null;
    styleInputCell(maxCell, PCT_NUM_FMT);
  }
}

function cohortSaturationFormula(origin) {
  const spend = monthCell(ROWS.marketing, origin);
  return `IF(OR($B$${ROWS.k}="",${spend}=0),0,$B$${ROWS.k}/($B$${ROWS.k}+${spend}))`;
}

function cohortLearningFormula(origin) {
  return `IF($B$${ROWS.learning}="",1,(1+$B$${ROWS.learning})^${origin})`;
}

function cohortFinalRoasFormula(origin, monthIndex, row) {
  if (monthIndex < origin) {
    return '0';
  }
  const mob = monthIndex - origin;
  const curveCell = monthCell(ROWS.curve, mob);
  return `IF(OR(${curveCell}="",${curveCell}=0),0,${curveCell}*$B$${row}*$C$${row})`;
}

function cohortGrossRevenueFormula(mode, origin, monthIndex, originCount) {
  if (monthIndex < origin) {
    return '0';
  }
  const spend = monthCell(ROWS.marketing, origin);
  if (mode === 'ltv') {
    const tables = dealIrrCohortTableRows(originCount, true);
    const finalRoas = monthCell(tables.saturationStart + origin, monthIndex);
    return `${spend}*${finalRoas}`;
  }
  const mob = monthIndex - origin;
  return `${spend}*${monthCell(ROWS.incremental, mob)}`;
}

function cohortEffectiveRevSharePctFormula(origin, monthCount) {
  const configured = `$B$${ROWS.revShare}`;
  const marketing = monthCell(ROWS.marketing, origin);
  const fundingMonthIndex = `${origin}+$B$${ROWS.marketingGrace}`;
  const draw = `IFERROR(INDEX(${monthRange(ROWS.customerFunding, monthCount)},1,${fundingMonthIndex}+1),0)`;
  const proRata = `IF(${marketing}=0,0,${draw}/${marketing})`;
  return `IF(OR(${configured}="",NOT(ISNUMBER(${configured}))),${proRata},${configured})`;
}

function cohortRevenueShareFormula(mode, origin, monthIndex, monthCount, originCount) {
  if (monthIndex < origin) {
    return '0';
  }
  const spend = monthCell(ROWS.marketing, origin);
  const rateRow = cohortShareRow(origin, originCount, mode);
  const effectiveRate = `$C$${rateRow}`;
  const netShare = `(1-$B$${ROWS.cogs})*${effectiveRate}`;
  let earned;
  if (mode === 'ltv') {
    const tables = dealIrrCohortTableRows(originCount, true);
    const finalRoas = monthCell(tables.saturationStart + origin, monthIndex);
    earned = `${spend}*${finalRoas}*${netShare}`;
  } else {
    const incrementalLookup = `INDEX(${monthRange(ROWS.incremental, monthCount)},1,${monthIndex}-${origin}+1)`;
    earned = `${spend}*${incrementalLookup}*${netShare}`;
  }
  return `IFERROR(${earned},0)`;
}

function cohortFeeAccrualFormula(origin, monthIndex, originCount, mode) {
  const fundingMonth = `${origin}+$B$${ROWS.marketingGrace}`;
  const irr = monthCell(ROWS.monthlyIrrPlan, origin);
  return `IF(${monthIndex}<=${fundingMonth},0,${monthCell(cohortExposureRow(origin, originCount, mode), monthIndex - 1)}*${irr})`;
}

function cohortAppliedRepaymentFormula(origin, monthIndex, monthCount, originCount, mode) {
  const fundingMonth = `${origin}+$B$${ROWS.marketingGrace}`;
  const startMonth = `${origin}+$B$${ROWS.marketingGrace}+$B$${ROWS.grace}`;
  const lastExposure = `IF(${monthIndex}=${fundingMonth},0,${monthCell(cohortExposureRow(origin, originCount, mode), monthIndex - 1)})`;
  const draw = `IF(${monthIndex}=${fundingMonth},${monthCell(ROWS.customerFunding, monthIndex)},0)`;
  const fee = monthCell(cohortFeeRow(origin, originCount, mode), monthIndex);
  const lagged = cohortLaggedRevenueShareFormula(origin, monthIndex, monthCount, originCount, mode);
  return `IF(${monthIndex}<${startMonth},0,MIN(${lagged},MAX(0,${lastExposure}+${draw}+${fee})))`;
}

function cohortEndingExposureFormula(origin, monthIndex, originCount, mode) {
  const fundingMonth = `${origin}+$B$${ROWS.marketingGrace}`;
  const lastExposure = `IF(${monthIndex}<${fundingMonth},0,IF(${monthIndex}=${fundingMonth},0,${monthCell(cohortExposureRow(origin, originCount, mode), monthIndex - 1)}))`;
  const draw = `IF(${monthIndex}=${fundingMonth},${monthCell(ROWS.customerFunding, monthIndex)},0)`;
  const fee = monthCell(cohortFeeRow(origin, originCount, mode), monthIndex);
  const repayment = monthCell(cohortAppliedRow(origin, originCount, mode), monthIndex);
  return `IF(${monthIndex}<${fundingMonth},0,MAX(0,${lastExposure}+${draw}+${fee}-${repayment}))`;
}

function styleDrawHelperSectionLabel(cell) {
  cell.font = { italic: true, color: { argb: EXCEL_THEME.sectionText } };
  cell.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'FFE7E6E6' },
  };
}

function writeDrawHelperSection(sheet) {
  const cell = sheet.getCell(ROWS.drawHelpersHeader, 1);
  cell.value = 'Draw calculation (helpers)';
  styleDrawHelperSectionLabel(cell);
  writeLabel(sheet, ROWS.fundableMarketing, 'Fundable marketing ($)');
  styleDrawHelperSectionLabel(sheet.getCell(ROWS.fundableMarketing, 1));
  writeLabel(sheet, ROWS.sameMonthRepaymentCredit, 'Same-month repayment credit ($)');
  styleDrawHelperSectionLabel(sheet.getCell(ROWS.sameMonthRepaymentCredit, 1));
  writeLabel(sheet, ROWS.drawExposureHeadroom, 'Draw exposure headroom ($)');
  styleDrawHelperSectionLabel(sheet.getCell(ROWS.drawExposureHeadroom, 1));
}

function writeCashflowRows(sheet, { monthCount, originCount, mode }) {
  writeLabel(sheet, ROWS.customerHeader, 'Customer cash');
  sheet.getCell(ROWS.customerHeader, 1).font = { bold: true, color: { argb: EXCEL_THEME.brandText } };
  sheet.getCell(ROWS.customerHeader, 1).fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: EXCEL_THEME.brand },
  };

  writeLabel(sheet, ROWS.customerMarketing, 'Marketing');
  writeLabel(sheet, ROWS.customerGross, 'Gross revenue');
  writeLabel(sheet, ROWS.customerNet, 'Net revenue');
  writeLabel(sheet, ROWS.twelveFundingPct, 'Twelve funding %');
  writeLabel(sheet, ROWS.customerFunding, 'Twelve funding');
  writeLabel(sheet, ROWS.customerRepayment, 'Twelve repayment');
  writeLabel(sheet, ROWS.customerFees, 'Twelve fees');
  writeLabel(sheet, ROWS.customerNetCf, 'Net cash flow');

  writeLabel(sheet, ROWS.twelveHeader, 'Twelve cash');
  sheet.getCell(ROWS.twelveHeader, 1).font = { bold: true, color: { argb: EXCEL_THEME.brandText } };
  sheet.getCell(ROWS.twelveHeader, 1).fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: EXCEL_THEME.brand },
  };
  writeLabel(sheet, ROWS.twelveOrigination, 'Origination (outflow)');
  writeLabel(sheet, ROWS.twelveRepayment, 'Repayment (inflow)');
  writeLabel(sheet, ROWS.twelveFee, 'Facility fee (inflow)');
  writeLabel(sheet, ROWS.twelveNet, 'Net cash flow');
  writeLabel(sheet, ROWS.interest, 'Monthly interest (accrued)');
  writeLabel(sheet, ROWS.exposure, 'Exposure');
  writeDrawHelperSection(sheet);

  const cohortTablesForSheet = dealIrrCohortTableRows(originCount, mode === 'ltv');

  for (let m = 0; m < monthCount; m += 1) {
    writeFormula(
      sheet,
      ROWS.customerMarketing,
      FIRST_MONTH_COL + m,
      customerMarketingOutflowFormula(m, monthCount),
      MONEY_NUM_FMT
    );
    writeFormula(
      sheet,
      ROWS.customerGross,
      FIRST_MONTH_COL + m,
      `SUM(${cohortColumnRange(cohortTablesForSheet.grossRevenueStart, m, originCount)})`,
      MONEY_NUM_FMT
    );
    writeFormula(
      sheet,
      ROWS.customerNet,
      FIRST_MONTH_COL + m,
      `${monthCell(ROWS.customerGross, m)}*(1-$B$${ROWS.cogs})`,
      MONEY_NUM_FMT
    );
    writeFormula(
      sheet,
      ROWS.twelveFundingPct,
      FIRST_MONTH_COL + m,
      twelveFundingPctFormula(m),
      PCT_NUM_FMT
    );
    writeFormula(
      sheet,
      ROWS.customerFunding,
      FIRST_MONTH_COL + m,
      drawFormula(m),
      MONEY_NUM_FMT
    );
    writeFormula(
      sheet,
      ROWS.customerRepayment,
      FIRST_MONTH_COL + m,
      `-${monthCell(ROWS.twelveRepayment, m)}`,
      MONEY_NUM_FMT
    );
    writeFormula(
      sheet,
      ROWS.customerFees,
      FIRST_MONTH_COL + m,
      `-${facilityFeeFormula(m)}`,
      MONEY_NUM_FMT
    );
    writeFormula(
      sheet,
      ROWS.customerNetCf,
      FIRST_MONTH_COL + m,
      `${monthCell(ROWS.customerMarketing, m)}+${monthCell(ROWS.customerNet, m)}+${monthCell(ROWS.customerFunding, m)}+${monthCell(ROWS.customerRepayment, m)}+${monthCell(ROWS.customerFees, m)}`,
      MONEY_NUM_FMT
    );

    writeFormula(
      sheet,
      ROWS.twelveOrigination,
      FIRST_MONTH_COL + m,
      `-${monthCell(ROWS.customerFunding, m)}`,
      MONEY_NUM_FMT
    );
    writeFormula(
      sheet,
      ROWS.twelveRepayment,
      FIRST_MONTH_COL + m,
      `SUM(${cohortColumnRange(
        cohortTables(originCount, mode).repaymentStart,
        m,
        originCount
      )})`,
      MONEY_NUM_FMT
    );
    writeFormula(
      sheet,
      ROWS.twelveFee,
      FIRST_MONTH_COL + m,
      `-${monthCell(ROWS.customerFees, m)}`,
      MONEY_NUM_FMT
    );
    writeFormula(
      sheet,
      ROWS.twelveNet,
      FIRST_MONTH_COL + m,
      `SUM(${monthCell(ROWS.twelveOrigination, m)}:${monthCell(ROWS.twelveFee, m)})`,
      MONEY_NUM_FMT
    );

    writeFormula(
      sheet,
      ROWS.interest,
      FIRST_MONTH_COL + m,
      `SUM(${cohortColumnRange(
        cohortTables(originCount, mode).feeStart,
        m,
        originCount
      )})`,
      MONEY_NUM_FMT
    );
    writeFormula(
      sheet,
      ROWS.exposure,
      FIRST_MONTH_COL + m,
      `SUM(${cohortColumnRange(
        cohortTables(originCount, mode).exposureStart,
        m,
        originCount
      )})`,
      MONEY_NUM_FMT
    );
    writeFormula(
      sheet,
      ROWS.fundableMarketing,
      FIRST_MONTH_COL + m,
      committedMarketingDueFormula(m, monthCount),
      MONEY_NUM_FMT
    );
    writeFormula(
      sheet,
      ROWS.sameMonthRepaymentCredit,
      FIRST_MONTH_COL + m,
      sameMonthRepaymentCreditSummaryFormula(m, originCount, mode),
      MONEY_NUM_FMT
    );
    writeFormula(
      sheet,
      ROWS.drawExposureHeadroom,
      FIRST_MONTH_COL + m,
      drawExposureHeadroomFormula(m),
      MONEY_NUM_FMT
    );
  }
}

function writeCohortGridHeader(sheet, row, title, monthCount) {
  writeLabel(sheet, row, title);
  sheet.getCell(row, 1).font = {
    bold: true,
    color: { argb: EXCEL_THEME.brandText },
  };
  sheet.getCell(row, 1).fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: EXCEL_THEME.brand },
  };
  for (let m = 0; m < monthCount; m += 1) {
    const cell = writeFormula(
      sheet,
      row,
      FIRST_MONTH_COL + m,
      calendarMonthFormula(m),
      DATE_NUM_FMT
    );
    cell.font = { bold: true, color: { argb: EXCEL_THEME.brandText } };
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: EXCEL_THEME.brand },
    };
    cell.alignment = { horizontal: 'center' };
  }
}

function writeCohortGridValues(sheet, startRow, originCount, monthCount, cellFormula, numFmt = MONEY_NUM_FMT) {
  for (let origin = 0; origin < originCount; origin += 1) {
    const row = startRow + origin;
    writeLabel(sheet, row, `Cohort month ${origin}`);
    for (let m = 0; m < monthCount; m += 1) {
      const formula = cellFormula(origin, m);
      if (formula === '0') {
        const cell = sheet.getCell(row, FIRST_MONTH_COL + m);
        cell.value = 0;
        cell.numFmt = numFmt;
      } else {
        writeFormula(sheet, row, FIRST_MONTH_COL + m, formula, numFmt);
      }
    }
  }
}

function styleCohortHelperHeaderCell(cell) {
  cell.font = { bold: true, color: { argb: EXCEL_THEME.brandText } };
  cell.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: EXCEL_THEME.brand },
  };
  cell.alignment = { horizontal: 'center' };
}

function writeCohortFinalRoasGrid(sheet, tables, originCount, monthCount) {
  writeLabel(sheet, tables.saturationHeader, 'Cohort final ROAS');
  styleCohortHelperHeaderCell(sheet.getCell(tables.saturationHeader, 1));

  const learningHeader = sheet.getCell(tables.saturationHeader, 2);
  learningHeader.value = 'Plan month learning';
  styleCohortHelperHeaderCell(learningHeader);

  const saturationHeader = sheet.getCell(tables.saturationHeader, 3);
  saturationHeader.value = 'Saturation K/(K+spend)';
  styleCohortHelperHeaderCell(saturationHeader);

  for (let m = 0; m < monthCount; m += 1) {
    const cell = writeFormula(
      sheet,
      tables.saturationHeader,
      FIRST_MONTH_COL + m,
      calendarMonthFormula(m),
      DATE_NUM_FMT
    );
    styleCohortHelperHeaderCell(cell);
  }

  for (let origin = 0; origin < originCount; origin += 1) {
    const row = tables.saturationStart + origin;
    writeLabel(sheet, row, `Cohort month ${origin}`);
    writeFormula(sheet, row, 2, cohortLearningFormula(origin), '0.0000');
    writeFormula(sheet, row, 3, cohortSaturationFormula(origin), PCT_NUM_FMT);
    for (let m = 0; m < monthCount; m += 1) {
      const formula = cohortFinalRoasFormula(origin, m, row);
      if (formula === '0') {
        const cell = sheet.getCell(row, FIRST_MONTH_COL + m);
        cell.value = 0;
        cell.numFmt = PCT_NUM_FMT;
      } else {
        writeFormula(sheet, row, FIRST_MONTH_COL + m, formula, PCT_NUM_FMT);
      }
    }
  }
}

function writeCohortRepaymentTable(sheet, { monthCount, originCount, mode }) {
  const tables = dealIrrCohortTableRows(originCount, mode === 'ltv');
  writeCohortGridHeader(sheet, tables.repaymentHeader, 'Cohort repayment', monthCount);
  const marketingHeader = sheet.getCell(tables.repaymentHeader, 2);
  marketingHeader.value = 'Marketing spend';
  styleCohortHelperHeaderCell(marketingHeader);
  const startHeader = sheet.getCell(tables.repaymentHeader, 3);
  startHeader.value = 'Repayment start (month)';
  styleCohortHelperHeaderCell(startHeader);

  for (let origin = 0; origin < originCount; origin += 1) {
    const row = tables.repaymentStart + origin;
    writeLabel(sheet, row, `Cohort month ${origin}`);
    writeFormula(sheet, row, 2, monthCell(ROWS.marketing, origin), MONEY_NUM_FMT);
    writeFormula(
      sheet,
      row,
      3,
      `${origin}+$B$${ROWS.marketingGrace}+$B$${ROWS.grace}`,
      '0'
    );
    for (let m = 0; m < monthCount; m += 1) {
      const formula = cohortAppliedRepaymentFormula(origin, m, monthCount, originCount, mode);
      if (formula === '0') {
        const cell = sheet.getCell(row, FIRST_MONTH_COL + m);
        cell.value = 0;
        cell.numFmt = MONEY_NUM_FMT;
      } else {
        writeFormula(sheet, row, FIRST_MONTH_COL + m, formula, MONEY_NUM_FMT);
      }
    }
  }
}

function writeCohortRevShareGrid(sheet, { monthCount, originCount, mode }) {
  const tables = dealIrrCohortTableRows(originCount, mode === 'ltv');
  writeCohortGridHeader(sheet, tables.revenueShareHeader, 'Cohort revenue share', monthCount);
  const revShareHeader = sheet.getCell(tables.revenueShareHeader, 3);
  revShareHeader.value = 'Rev share % charged';
  styleCohortHelperHeaderCell(revShareHeader);

  for (let origin = 0; origin < originCount; origin += 1) {
    const row = tables.revenueShareStart + origin;
    writeLabel(sheet, row, `Cohort month ${origin}`);
    writeFormula(
      sheet,
      row,
      3,
      cohortEffectiveRevSharePctFormula(origin, monthCount),
      PCT_NUM_FMT
    );
    for (let m = 0; m < monthCount; m += 1) {
      const formula = cohortRevenueShareFormula(mode, origin, m, monthCount, originCount);
      if (formula === '0') {
        const cell = sheet.getCell(row, FIRST_MONTH_COL + m);
        cell.value = 0;
        cell.numFmt = MONEY_NUM_FMT;
      } else {
        writeFormula(sheet, row, FIRST_MONTH_COL + m, formula, MONEY_NUM_FMT);
      }
    }
  }
}

function writeCohortHelperTables(sheet, { monthCount, originCount, mode }) {
  const tables = dealIrrCohortTableRows(originCount, mode === 'ltv');
  if (mode === 'ltv') {
    writeCohortFinalRoasGrid(sheet, tables, originCount, monthCount);
  }
  writeCohortGridHeader(sheet, tables.grossRevenueHeader, 'Cohort gross revenue', monthCount);
  writeCohortGridValues(sheet, tables.grossRevenueStart, originCount, monthCount, (origin, monthIndex) =>
    cohortGrossRevenueFormula(mode, origin, monthIndex, originCount)
  );
  writeCohortRevShareGrid(sheet, { monthCount, originCount, mode });
  writeCohortGridHeader(sheet, tables.exposureHeader, 'Cohort exposure', monthCount);
  writeCohortGridValues(sheet, tables.exposureStart, originCount, monthCount, (origin, monthIndex) =>
    cohortEndingExposureFormula(origin, monthIndex, originCount, mode)
  );
  writeCohortGridHeader(sheet, tables.feeHeader, 'Cohort fee', monthCount);
  writeCohortGridValues(sheet, tables.feeStart, originCount, monthCount, (origin, monthIndex) =>
    cohortFeeAccrualFormula(origin, monthIndex, originCount, mode)
  );
  writeLabel(sheet, tables.feeTotal, 'Total');
  sheet.getCell(tables.feeTotal, 1).font = { bold: true, color: { argb: EXCEL_THEME.sectionText } };
  for (let m = 0; m < monthCount; m += 1) {
    writeFormula(
      sheet,
      tables.feeTotal,
      FIRST_MONTH_COL + m,
      `SUM(${cohortColumnRange(tables.feeStart, m, originCount)})`,
      MONEY_NUM_FMT
    );
  }
  writeCohortGridHeader(sheet, tables.laggedShareHeader, 'Cohort lagged revenue share', monthCount);
  writeCohortGridValues(sheet, tables.laggedShareStart, originCount, monthCount, (origin, monthIndex) =>
    cohortLaggedRevenueShareFormula(origin, monthIndex, monthCount, originCount, mode)
  );
  writeCohortGridHeader(
    sheet,
    tables.preDrawCreditHeader,
    'Cohort pre-draw repayment credit',
    monthCount
  );
  writeCohortGridValues(sheet, tables.preDrawCreditStart, originCount, monthCount, (origin, monthIndex) =>
    cohortPreDrawRepaymentCreditFormula(origin, monthIndex, originCount, mode)
  );
  writeCohortRepaymentTable(sheet, { monthCount, originCount, mode });
}

function writeRoasSheet(workbook, inputs) {
  const sheet = workbook.addWorksheet(DEAL_IRR_SHEETS.ROAS);
  const monthCount = resolveMonthCount(inputs);
  const originCount = Math.max(1, inputs.marketing.length);

  sheet.getColumn(1).width = 32;
  sheet.getColumn(2).width = 22;
  sheet.getColumn(CALC_LABEL_COL).width = 26;
  sheet.getColumn(CALC_VALUE_COL).width = 16;

  writeSharedInputs(sheet, inputs, { includeHill: false });
  writeOutputFormulas(sheet, monthCount);
  writeTimelineHeaders(sheet, monthCount);
  writeMonthlyIrrPlanRow(sheet, inputs, monthCount);
  writeMarketingRow(sheet, inputs.marketing, monthCount);
  writeRoasCurve(sheet, inputs.cumulativeRoas, monthCount);
  writeCashflowRows(sheet, {
    monthCount,
    originCount,
    mode: 'roas',
  });
  writeFundedIrrPlanRow(sheet, originCount);
  writeCohortHelperTables(sheet, {
    monthCount,
    originCount,
    mode: 'roas',
  });
  return sheet;
}

function writeLtvSheet(workbook, inputs) {
  const sheet = workbook.addWorksheet(DEAL_IRR_SHEETS.LTV);
  const monthCount = resolveMonthCount(inputs);
  const originCount = Math.max(1, inputs.marketing.length);

  sheet.getColumn(1).width = 32;
  sheet.getColumn(2).width = 22;
  sheet.getColumn(CALC_LABEL_COL).width = 26;
  sheet.getColumn(CALC_VALUE_COL).width = 16;

  writeSharedInputs(sheet, inputs, { includeHill: true });
  writeOutputFormulas(sheet, monthCount);
  writeTimelineHeaders(sheet, monthCount);
  writeMonthlyIrrPlanRow(sheet, inputs, monthCount);
  writeMarketingRow(sheet, inputs.marketing, monthCount);
  writeLtvCurve(sheet, inputs, monthCount);
  writeCashflowRows(sheet, {
    monthCount,
    originCount,
    mode: 'ltv',
  });
  writeFundedIrrPlanRow(sheet, originCount);
  writeCohortHelperTables(sheet, {
    monthCount,
    originCount,
    mode: 'ltv',
  });
  return sheet;
}

function sheetRef(sheetName, a1) {
  return `'${sheetName}'!${a1}`;
}

function inputRef(sheetName, row) {
  return sheetRef(sheetName, `B${row}`);
}

function calcRef(sheetName, row) {
  return sheetRef(sheetName, `${colLetter(CALC_VALUE_COL)}${row}`);
}

function calendarYearSpan(firstMarketingMonth, monthCount) {
  const startMonth0 =
    firstMarketingMonth instanceof Date && !Number.isNaN(firstMarketingMonth.getTime())
      ? firstMarketingMonth.getMonth()
      : 0;
  const endMonth0 = startMonth0 + Math.max(1, monthCount) - 1;
  return Math.floor(endMonth0 / 12) - Math.floor(startMonth0 / 12) + 1;
}

function yearAggFormula(sheetName, dataRow, yearHeaderRow, yearIndex, monthCount, agg) {
  const yearRef = `${colLetter(IRR_SUMMARY_YEAR_START_COL + yearIndex)}${yearHeaderRow}`;
  const dateRange = sheetRef(sheetName, monthRange(ROWS.calendarHeader, monthCount));
  const valueRange = sheetRef(sheetName, monthRange(dataRow, monthCount));
  const fromDate = `DATE(${yearRef},1,1)`;
  const toDate = `DATE(${yearRef}+1,1,1)`;
  if (agg === 'max') {
    return `IFERROR(MAXIFS(${valueRange},${dateRange},">="&${fromDate},${dateRange},"<"&${toDate}),0)`;
  }
  if (agg === 'avg') {
    return `IFERROR(AVERAGEIFS(${valueRange},${dateRange},">="&${fromDate},${dateRange},"<"&${toDate},${valueRange},">0"),0)`;
  }
  return `SUMIFS(${valueRange},${dateRange},">="&${fromDate},${dateRange},"<"&${toDate})`;
}

function irrSummaryLayout(startRow) {
  return {
    title: startRow,
    maxExposure: startRow + 1,
    commitment: startRow + 2,
    headlineOrigination: startRow + 3,
    creditHeader: startRow + 5,
    feeHeader: startRow + 6,
    financingFee: startRow + 7,
    facilityFee: startRow + 8,
    creditTotal: startRow + 9,
    yearHeader: startRow + 11,
    cashAtRiskMax: startRow + 12,
    netCashReturnMax: startRow + 13,
    cashReturnMax: startRow + 14,
    cashAtRiskAvg: startRow + 16,
    netCashReturnAvg: startRow + 17,
    cashReturnAvg: startRow + 18,
    outflow: startRow + 20,
    inflow: startRow + 21,
    netCash: startRow + 22,
    cashOnCash: startRow + 23,
    financingFeeCash: startRow + 25,
    facilityFeeCash: startRow + 26,
    totalFee: startRow + 27,
    feeOnAvg: startRow + 28,
    feeOnMax: startRow + 29,
  };
}

function styleSummaryTitle(sheet, row, title) {
  const cell = sheet.getCell(row, IRR_SUMMARY_LABEL_COL);
  cell.value = title;
  cell.font = { bold: true, size: 14, color: { argb: EXCEL_THEME.brandText } };
  cell.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: EXCEL_THEME.brand },
  };
  sheet.mergeCells(row, IRR_SUMMARY_LABEL_COL, row, IRR_SUMMARY_IRR_COL);
}

function styleSummarySection(sheet, row, label) {
  const cell = sheet.getCell(row, IRR_SUMMARY_LABEL_COL);
  cell.value = label;
  cell.font = { bold: true, color: { argb: EXCEL_THEME.brandText } };
  cell.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: EXCEL_THEME.brand },
  };
  sheet.mergeCells(row, IRR_SUMMARY_LABEL_COL, row, IRR_SUMMARY_IRR_COL);
}

function addSummarySeparator(sheet, row, yearCount) {
  const lastCol = Math.max(
    IRR_SUMMARY_IRR_COL,
    IRR_SUMMARY_YEAR_START_COL + yearCount - 1
  );
  const edge = { style: 'medium', color: { argb: EXCEL_THEME.sectionText } };
  for (let col = IRR_SUMMARY_LABEL_COL; col <= lastCol; col += 1) {
    const cell = sheet.getCell(row, col);
    cell.border = {
      ...(cell.border || {}),
      top: edge,
    };
  }
}

function writeSummaryLabel(sheet, row, label, { bold = false } = {}) {
  const cell = sheet.getCell(row, IRR_SUMMARY_LABEL_COL);
  cell.value = label;
  cell.font = {
    bold,
    color: { argb: EXCEL_THEME.sectionText },
  };
}

function writeYearFormulas(
  sheet,
  row,
  yearCount,
  monthCount,
  src,
  dataRow,
  agg,
  numFmt,
  yearHeaderRow
) {
  for (let yearIndex = 0; yearIndex < yearCount; yearIndex += 1) {
    writeFormula(
      sheet,
      row,
      IRR_SUMMARY_YEAR_START_COL + yearIndex,
      yearAggFormula(src, dataRow, yearHeaderRow, yearIndex, monthCount, agg),
      numFmt
    );
  }
}

function writeIrrSummaryBlock(sheet, startRow, { title, src, monthCount, yearCount, originCount }) {
  const r = irrSummaryLayout(startRow);
  const feeTotalRow = cohortTables(originCount, src === DEAL_IRR_SHEETS.LTV ? 'ltv' : 'roas').feeTotal;
  const facilitySize = inputRef(src, ROWS.facilitySize);
  const maxDraw = inputRef(src, ROWS.maxMonthlyDraw);
  const commitment = inputRef(src, ROWS.commitment);
  const maxExposure = calcRef(src, ROWS.maxExposureCalc);
  const avgExposure = calcRef(src, ROWS.avgExposure);
  const outflow = calcRef(src, ROWS.outflow);
  const inflow = calcRef(src, ROWS.inflow);
  const netCash = calcRef(src, ROWS.netCash);
  const dealIrr = calcRef(src, ROWS.irr);
  const fundedIrrRange = sheetRef(src, monthRange(ROWS.fundedIrrPlan, originCount));
  const facilityFeePct = inputRef(src, ROWS.facilityFeePct);
  const financingRate = `${colLetter(IRR_SUMMARY_VALUE_COL)}${r.financingFee}`;
  const financingIrr = `${colLetter(IRR_SUMMARY_IRR_COL)}${r.financingFee}`;
  const facilityIrr = `${colLetter(IRR_SUMMARY_IRR_COL)}${r.facilityFee}`;
  const maxCell = `${colLetter(IRR_SUMMARY_VALUE_COL)}${r.maxExposure}`;
  const avgCell = `${colLetter(IRR_SUMMARY_VALUE_COL)}${r.cashAtRiskAvg}`;
  const netReturnCell = `${colLetter(IRR_SUMMARY_VALUE_COL)}${r.netCashReturnMax}`;
  const outflowCell = `${colLetter(IRR_SUMMARY_VALUE_COL)}${r.outflow}`;
  const inflowCell = `${colLetter(IRR_SUMMARY_VALUE_COL)}${r.inflow}`;
  const financingFeeCashCell = `${colLetter(IRR_SUMMARY_VALUE_COL)}${r.financingFeeCash}`;
  const facilityFeeCashCell = `${colLetter(IRR_SUMMARY_VALUE_COL)}${r.facilityFeeCash}`;
  const totalFeeCell = `${colLetter(IRR_SUMMARY_VALUE_COL)}${r.totalFee}`;

  styleSummaryTitle(sheet, r.title, title);

  writeSummaryLabel(sheet, r.maxExposure, 'Max exposure (estimated)');
  writeFormula(sheet, r.maxExposure, IRR_SUMMARY_VALUE_COL, maxExposure, MONEY_NUM_FMT);

  writeSummaryLabel(sheet, r.commitment, 'Commitment (mos.)');
  writeFormula(sheet, r.commitment, IRR_SUMMARY_VALUE_COL, commitment);

  writeSummaryLabel(sheet, r.headlineOrigination, 'Max facility size (headline origination)');
  writeFormula(
    sheet,
    r.headlineOrigination,
    IRR_SUMMARY_VALUE_COL,
    `IF(OR(${maxDraw}="",${commitment}=""),${facilitySize},${maxDraw}*${commitment})`,
    MONEY_NUM_FMT
  );

  styleSummarySection(sheet, r.creditHeader, 'Credit Return');
  writeSummaryLabel(sheet, r.feeHeader, 'Fee', { bold: true });
  sheet.getCell(r.feeHeader, IRR_SUMMARY_VALUE_COL).value = 'Rate';
  sheet.getCell(r.feeHeader, IRR_SUMMARY_BASIS_COL).value = 'Calculation basis';
  sheet.getCell(r.feeHeader, IRR_SUMMARY_IRR_COL).value = 'IRR';
  [IRR_SUMMARY_VALUE_COL, IRR_SUMMARY_BASIS_COL, IRR_SUMMARY_IRR_COL].forEach((col) => {
    sheet.getCell(r.feeHeader, col).font = { bold: true, color: { argb: EXCEL_THEME.sectionText } };
  });

  writeSummaryLabel(sheet, r.financingFee, 'Financing fee');
  writeFormula(
    sheet,
    r.financingFee,
    IRR_SUMMARY_VALUE_COL,
    `IFERROR(AVERAGE(${fundedIrrRange}),0)`,
    PCT_NUM_FMT
  );
  sheet.getCell(r.financingFee, IRR_SUMMARY_BASIS_COL).value = 'Monthly exposure';
  writeFormula(
    sheet,
    r.financingFee,
    IRR_SUMMARY_IRR_COL,
    `IFERROR((1+${financingRate})^12-1,"")`,
    PCT_NUM_FMT
  );

  writeSummaryLabel(sheet, r.facilityFee, 'Facility fee');
  writeFormula(sheet, r.facilityFee, IRR_SUMMARY_VALUE_COL, facilityFeePct, PCT_NUM_FMT);
  sheet.getCell(r.facilityFee, IRR_SUMMARY_BASIS_COL).value = 'Total headline origination number';
  writeFormula(
    sheet,
    r.facilityFee,
    IRR_SUMMARY_IRR_COL,
    `IFERROR(${dealIrr}-${financingIrr},0)`,
    PCT_NUM_FMT
  );

  writeSummaryLabel(sheet, r.creditTotal, 'Total', { bold: true });
  writeFormula(
    sheet,
    r.creditTotal,
    IRR_SUMMARY_IRR_COL,
    `${financingIrr}+${facilityIrr}`,
    PCT_NUM_FMT
  );
  sheet.getCell(r.creditTotal, IRR_SUMMARY_IRR_COL).font = { bold: true };

  for (let yearIndex = 0; yearIndex < yearCount; yearIndex += 1) {
    const yearCell = writeFormula(
      sheet,
      r.yearHeader,
      IRR_SUMMARY_YEAR_START_COL + yearIndex,
      `YEAR(${inputRef(src, ROWS.firstMarketingMonth)})+${yearIndex}`
    );
    yearCell.font = { bold: true, color: { argb: EXCEL_THEME.sectionText } };
  }

  writeSummaryLabel(sheet, r.cashAtRiskMax, 'Cash at risk (max exposure est.)');
  writeFormula(sheet, r.cashAtRiskMax, IRR_SUMMARY_VALUE_COL, maxExposure, MONEY_NUM_FMT);
  writeYearFormulas(
    sheet,
    r.cashAtRiskMax,
    yearCount,
    monthCount,
    src,
    ROWS.exposure,
    'max',
    MONEY_NUM_FMT,
    r.yearHeader
  );

  writeSummaryLabel(sheet, r.netCashReturnMax, 'Net cash return');
  writeFormula(sheet, r.netCashReturnMax, IRR_SUMMARY_VALUE_COL, netCash, MONEY_NUM_FMT);

  writeSummaryLabel(sheet, r.cashReturnMax, 'Cash return on max exposure');
  writeFormula(
    sheet,
    r.cashReturnMax,
    IRR_SUMMARY_VALUE_COL,
    `IFERROR(1+${netReturnCell}/${maxCell},"")`,
    MULTIPLIER_NUM_FMT
  );

  writeSummaryLabel(sheet, r.cashAtRiskAvg, 'Cash at risk (Avg exposure est.)');
  writeFormula(sheet, r.cashAtRiskAvg, IRR_SUMMARY_VALUE_COL, avgExposure, MONEY_NUM_FMT);
  writeYearFormulas(
    sheet,
    r.cashAtRiskAvg,
    yearCount,
    monthCount,
    src,
    ROWS.exposure,
    'avg',
    MONEY_NUM_FMT,
    r.yearHeader
  );

  writeSummaryLabel(sheet, r.netCashReturnAvg, 'Net cash return');
  writeFormula(sheet, r.netCashReturnAvg, IRR_SUMMARY_VALUE_COL, netCash, MONEY_NUM_FMT);

  writeSummaryLabel(sheet, r.cashReturnAvg, 'Cash return on Avg exposure est.');
  writeFormula(
    sheet,
    r.cashReturnAvg,
    IRR_SUMMARY_VALUE_COL,
    `IFERROR(1+${netReturnCell}/${avgCell},"")`,
    MULTIPLIER_NUM_FMT
  );

  writeSummaryLabel(sheet, r.outflow, 'Total Growth Investment');
  writeFormula(sheet, r.outflow, IRR_SUMMARY_VALUE_COL, outflow, MONEY_NUM_FMT);
  writeYearFormulas(
    sheet,
    r.outflow,
    yearCount,
    monthCount,
    src,
    ROWS.twelveOrigination,
    'sum',
    MONEY_NUM_FMT,
    r.yearHeader
  );

  writeSummaryLabel(sheet, r.inflow, 'Total Returns');
  writeFormula(sheet, r.inflow, IRR_SUMMARY_VALUE_COL, inflow, MONEY_NUM_FMT);
  for (let yearIndex = 0; yearIndex < yearCount; yearIndex += 1) {
    writeFormula(
      sheet,
      r.inflow,
      IRR_SUMMARY_YEAR_START_COL + yearIndex,
      `${yearAggFormula(src, ROWS.twelveRepayment, r.yearHeader, yearIndex, monthCount, 'sum')}+${yearAggFormula(src, ROWS.twelveFee, r.yearHeader, yearIndex, monthCount, 'sum')}`,
      MONEY_NUM_FMT
    );
  }

  writeSummaryLabel(sheet, r.netCash, 'Net cash');
  writeFormula(sheet, r.netCash, IRR_SUMMARY_VALUE_COL, netCash, MONEY_NUM_FMT);
  writeYearFormulas(
    sheet,
    r.netCash,
    yearCount,
    monthCount,
    src,
    ROWS.twelveNet,
    'sum',
    MONEY_NUM_FMT,
    r.yearHeader
  );
  addSummarySeparator(sheet, r.netCash, yearCount);

  writeSummaryLabel(sheet, r.cashOnCash, 'Cash-on-cash');
  writeFormula(
    sheet,
    r.cashOnCash,
    IRR_SUMMARY_VALUE_COL,
    `IFERROR(${inflowCell}/ABS(${outflowCell}),"")`,
    MULTIPLIER_NUM_FMT
  );

  writeSummaryLabel(sheet, r.financingFeeCash, 'Fee');
  writeFormula(
    sheet,
    r.financingFeeCash,
    IRR_SUMMARY_VALUE_COL,
    `SUM(${sheetRef(src, monthRange(feeTotalRow, monthCount))})`,
    MONEY_NUM_FMT
  );
  writeYearFormulas(
    sheet,
    r.financingFeeCash,
    yearCount,
    monthCount,
    src,
    feeTotalRow,
    'sum',
    MONEY_NUM_FMT,
    r.yearHeader
  );

  writeSummaryLabel(sheet, r.facilityFeeCash, 'Facility Fee');
  writeFormula(
    sheet,
    r.facilityFeeCash,
    IRR_SUMMARY_VALUE_COL,
    `SUM(${sheetRef(src, monthRange(ROWS.twelveFee, monthCount))})`,
    MONEY_NUM_FMT
  );
  writeYearFormulas(
    sheet,
    r.facilityFeeCash,
    yearCount,
    monthCount,
    src,
    ROWS.twelveFee,
    'sum',
    MONEY_NUM_FMT,
    r.yearHeader
  );

  writeSummaryLabel(sheet, r.totalFee, 'Total Fee');
  writeFormula(
    sheet,
    r.totalFee,
    IRR_SUMMARY_VALUE_COL,
    `${financingFeeCashCell}+${facilityFeeCashCell}`,
    MONEY_NUM_FMT
  );
  for (let yearIndex = 0; yearIndex < yearCount; yearIndex += 1) {
    const feeYear = `${colLetter(IRR_SUMMARY_YEAR_START_COL + yearIndex)}${r.financingFeeCash}`;
    const facilityYear = `${colLetter(IRR_SUMMARY_YEAR_START_COL + yearIndex)}${r.facilityFeeCash}`;
    writeFormula(
      sheet,
      r.totalFee,
      IRR_SUMMARY_YEAR_START_COL + yearIndex,
      `${feeYear}+${facilityYear}`,
      MONEY_NUM_FMT
    );
  }
  addSummarySeparator(sheet, r.totalFee, yearCount);

  writeSummaryLabel(sheet, r.feeOnAvg, 'Total Fee/ Avg exposure est.');
  writeFormula(
    sheet,
    r.feeOnAvg,
    IRR_SUMMARY_VALUE_COL,
    `IFERROR(1+${totalFeeCell}/${avgCell},"")`,
    MULTIPLIER_NUM_FMT
  );

  writeSummaryLabel(sheet, r.feeOnMax, 'Total Fee/ max exposure est');
  writeFormula(
    sheet,
    r.feeOnMax,
    IRR_SUMMARY_VALUE_COL,
    `IFERROR(1+${totalFeeCell}/${maxCell},"")`,
    MULTIPLIER_NUM_FMT
  );
}

function writeIrrSummarySheet(workbook, sheetName, inputs, { title, src }) {
  const sheet = workbook.addWorksheet(sheetName);
  const monthCount = resolveMonthCount(inputs);
  const originCount = Math.max(1, inputs.marketing.length);
  const yearCount = Math.min(
    IRR_SUMMARY_MAX_YEARS,
    Math.max(1, calendarYearSpan(inputs.firstMarketingMonth, monthCount))
  );

  sheet.getColumn(1).width = 4;
  sheet.getColumn(IRR_SUMMARY_LABEL_COL).width = 42;
  sheet.getColumn(IRR_SUMMARY_VALUE_COL).width = 16;
  sheet.getColumn(IRR_SUMMARY_BASIS_COL).width = 36;
  sheet.getColumn(IRR_SUMMARY_IRR_COL).width = 14;
  for (let yearIndex = 0; yearIndex < yearCount; yearIndex += 1) {
    sheet.getColumn(IRR_SUMMARY_YEAR_START_COL + yearIndex).width = Math.max(
      sheet.getColumn(IRR_SUMMARY_YEAR_START_COL + yearIndex).width || 0,
      14
    );
  }

  writeIrrSummaryBlock(sheet, 1, {
    title,
    src,
    monthCount,
    yearCount,
    originCount,
  });
  return sheet;
}

function writeIrrSummaries(workbook, inputs) {
  writeIrrSummarySheet(workbook, DEAL_IRR_SHEETS.IRR_ROAS, inputs, {
    title: 'IRR Summary — ROAS vector',
    src: DEAL_IRR_SHEETS.ROAS,
  });
  writeIrrSummarySheet(workbook, DEAL_IRR_SHEETS.IRR_HILL, inputs, {
    title: 'IRR Summary — LTV curve (Hill)',
    src: DEAL_IRR_SHEETS.LTV,
  });
}

function sanitizeFilenamePart(value) {
  return String(value || '')
    .trim()
    .replace(/[^\w.-]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

export function buildDealIrrModelFilename(filenameBase = 'quick-cashflow') {
  const base = sanitizeFilenamePart(filenameBase) || 'quick-cashflow';
  return `${base}-deal-irr-model.xlsx`;
}

export async function buildDealIrrModelWorkbook({ state, hillModel = null } = {}) {
  const ExcelJS = (await import('exceljs')).default;
  const resolvedHill =
    hillModel ??
    (hasCohortSheet(state?.cohortSheet) ? fitHillRoasModel(state.cohortSheet) : null);
  const inputs = resolveDealIrrModelInputs(state, resolvedHill);
  const workbook = new ExcelJS.Workbook();
  writeRoasSheet(workbook, inputs);
  writeLtvSheet(workbook, inputs);
  writeIrrSummaries(workbook, inputs);
  return workbook;
}

export async function buildDealIrrModelExcelBlob({
  state,
  hillModel = null,
  filenameBase = 'quick-cashflow',
} = {}) {
  const workbook = await buildDealIrrModelWorkbook({ state, hillModel });
  const buffer = await workbook.xlsx.writeBuffer();
  return {
    blob: new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    }),
    filename: buildDealIrrModelFilename(filenameBase),
  };
}

export async function downloadDealIrrModelExcel(options = {}) {
  const built = await buildDealIrrModelExcelBlob(options);
  saveAs(built.blob, built.filename);
}

export { ROWS as DEAL_IRR_ROWS };
